import React, { useEffect } from 'react'
import Modal from '../Modal/ModalTS'

import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import { useNavigate } from 'react-router'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useAppSelector } from '../../hooks/useStore'
import googleAnalytics4 from '../../lib/googleAnalytics'
import tts from '../../lib/TTS'
import FocusableButtonTS from '../FocusableButton/FocusableButtonTS'
import { useSidePanel } from '../SidePanel/SidePanelProvider'
import type { LocalizeStationModalProps } from './types'

const LOCALIZE_STATION_MODAL_FOCUS_KEY = 'LOCALIZE_STATION_MODAL'

const START_WATCHING_BUTTON_CONFIG = {
  label: 'Start Watching',
  focusKey: 'START_WATCHING_BUTTON',
  ttsMessage: `Focused on start watching button`,
  ttsSelect: `Selected start watching button.`,
}
const CHANGE_STATION_BUTTON_CONFIG = {
  label: 'Change Station',
  focusKey: 'CHANGE_STATION_BUTTON',
  ttsMessage: `Focused on change station button`,
  ttsSelect: `Selected change station button.`,
}

function LocalizeStationModal(props: LocalizeStationModalProps) {
  const { ref, focusKey } = useHybridFocusable({
    focusKey: LOCALIZE_STATION_MODAL_FOCUS_KEY,
  })
  const navigate = useNavigate()
  const { setKeepSidePanelOpened, focusSidePanel, setAvoidStealFocus } =
    useSidePanel()
  const { short_common_name: stationShortCommonName } = useAppSelector(
    (store) => store.station
  )
  const modalMessage = `Hi neighbor! ${stationShortCommonName} is your local station.`

  const readTTS = (message: string) => {
    tts.addTextToRead(message)
  }

  const toggleBlur = (toggleAction: boolean) => {
    const sidePanelLayout = document.getElementById(
      'side-panel-layout'
    ) as HTMLElement
    if (sidePanelLayout) {
      sidePanelLayout.style.filter = toggleAction ? 'blur(10px)' : 'none'
    }
  }

  const sendAutoLocalizationEvent = () => {
    googleAnalytics4.sendCustomEvent('feature_tracking', {
      feature_category: 'station localization',
      feature_name: 're-localization prompt',
      object_name: 'modal',
      object_action: 'impression',
      object_action_behavior: 'display content',
    })
  }

  const sendDismissActionEvent = () => {
    googleAnalytics4.sendCustomEvent('feature_tracking', {
      feature_category: 'station localization',
      feature_name: 're-localization prompt',
      object_name: 'dismiss button',
      object_type: 'button',
      object_position: '1',
      object_text: 'Start Watching',
      object_action: 'button press',
      object_action_behavior: 'dismiss',
    })
  }

  const sendChangeStationActionEvent = () => {
    googleAnalytics4.sendCustomEvent('feature_tracking', {
      feature_category: 'station localization',
      feature_name: 're-localization prompt',
      object_name: 'change station button',
      object_type: 'button',
      object_position: '2',
      object_text: 'Change Station',
      object_action: 'button press',
      object_action_behavior: 'next screen',
    })
  }

  const handleDismissModal = () => {
    tts.pushTextToRead(START_WATCHING_BUTTON_CONFIG.ttsSelect)
    toggleBlur(false)
    sendDismissActionEvent()
    props.dismissModalHandler()
  }

  const handleChangeStation = () => {
    tts.pushTextToRead(CHANGE_STATION_BUTTON_CONFIG.ttsSelect)
    sendChangeStationActionEvent()
    navigate('/change-station')
  }

  useEffect(() => {
    tts.pushTextToRead(modalMessage)
    toggleBlur(true)
    setKeepSidePanelOpened(true)
    setAvoidStealFocus(true)
    sendAutoLocalizationEvent()

    return () => {
      setAvoidStealFocus(false)
      setKeepSidePanelOpened(false)
      focusSidePanel()
    }
  }, [])

  useKeyDown(
    KeyTypeEnum.Back,
    () => {
      handleDismissModal()
    },
    {
      stopPropagation: true,
    }
  )

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="localization-modal" ref={ref}>
        <Modal error hideStationLogo>
          <p className="localization-modal__message">{modalMessage}</p>
          <div className="localization-modal__buttons">
            <FocusableButtonTS
              onEnterPress={handleDismissModal}
              onFocus={() => readTTS(START_WATCHING_BUTTON_CONFIG.ttsMessage)}
              focusKey={START_WATCHING_BUTTON_CONFIG.focusKey}
              stealFocus
            >
              {START_WATCHING_BUTTON_CONFIG.label}
            </FocusableButtonTS>
            <FocusableButtonTS
              onEnterPress={handleChangeStation}
              onFocus={() => readTTS(CHANGE_STATION_BUTTON_CONFIG.ttsMessage)}
              focusKey={CHANGE_STATION_BUTTON_CONFIG.focusKey}
              className="localization-modal__change-station-button"
            >
              {CHANGE_STATION_BUTTON_CONFIG.label}
            </FocusableButtonTS>
          </div>
        </Modal>
      </div>
    </FocusContext.Provider>
  )
}

export default LocalizeStationModal
